import axiosInstance from '../lib/axios'
import { openEventStream, type StreamErrorInfo } from '../lib/sse'
import { API_ENDPOINTS } from '../constants/api'
import type {
  Call,
  CallAudioUrl,
  CallListResponse,
  CallMessage,
  CallMessageListResponse,
  Order,
} from '../types/api.types'

export interface CallListParams {
  // YYYY-MM-DD, UTC. Omit for the most recent calls regardless of date.
  day?: string
  limit?: number
  offset?: number
}

export interface CallStreamHandlers {
  onCall: (call: Call) => void
  // Fires on placement, on every status change, and on a kitchen print ack.
  onOrder: (order: Order) => void
  onOpen?: () => void
  onError?: (info: StreamErrorInfo) => void
  onAuthRevoked?: () => void
}

export const CALL_PAGE_SIZE = 100

export const callsService = {
  // Newest first, always. The response `total` is this page's length, not the
  // size of the result set — page until a response comes back short.
  async list(propertyId: number, params: CallListParams = {}): Promise<Call[]> {
    const query = new URLSearchParams({ property_id: String(propertyId) })
    if (params.day) query.set('day', params.day)
    query.set('limit', String(params.limit ?? CALL_PAGE_SIZE))
    if (params.offset) query.set('offset', String(params.offset))

    const { data } = await axiosInstance.get<CallListResponse>(`${API_ENDPOINTS.CALLS}?${query}`)
    return data.calls ?? []
  },

  // In speaking order by turn_index. An empty list is valid — a call where
  // nobody spoke is still evidence.
  async messages(callId: number): Promise<CallMessage[]> {
    const { data } = await axiosInstance.get<CallMessageListResponse>(
      API_ENDPOINTS.CALL_MESSAGES(callId),
    )
    return data.messages ?? []
  },

  // A freshly minted SAS link that dies in an hour. Call this when the user hits
  // play, never when the list renders, and do not stash the result anywhere that
  // outlives the player.
  async audioUrl(callId: number): Promise<CallAudioUrl> {
    const { data } = await axiosInstance.get<CallAudioUrl>(API_ENDPOINTS.CALL_AUDIO_URL(callId))
    return data
  },

  // ponytail: no branch for the 503 the server sends when Redis is down —
  // EventSource surfaces it as an untyped transport error like any other, and
  // the caller's answer is the same either way: stop claiming Live and poll.
  streamLive(propertyId: number, handlers: CallStreamHandlers): () => void {
    return openEventStream(API_ENDPOINTS.RESTAURANT_STREAM(propertyId), {
      events: {
        call: (data) => handlers.onCall(data as Call),
        order: (data) => handlers.onOrder(data as Order),
      },
      onOpen: handlers.onOpen,
      onError: handlers.onError,
      onAuthRevoked: handlers.onAuthRevoked,
    })
  },
}
